import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import QRCode from 'qrcode.react';

const { FiUsers, FiPlus, FiEdit, FiTrash2, FiEye, FiLogOut, FiSearch, FiFilter, 
  FiDownload, FiSettings, FiSave, FiMove, FiMaximize, FiMinimize, FiType, 
  FiLayers, FiMapPin, FiQrCode, FiCheck, FiAlertTriangle } = FiIcons;

const AdminDashboard = () => {
  const { user, cities, logout, addCity, updateCity, deleteCity } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [showAddCity, setShowAddCity] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cityToDelete, setCityToDelete] = useState(null);
  const [newCity, setNewCity] = useState({ email: '', cityName: '', subscription: 'trial' });
  const [cityConfig, setCityConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated and is a super admin
    if (!user) {
      navigate('/');
      return;
    }
    if (user.email !== 'cityshot@clickbooster.app') {
      navigate('/');
      return;
    }
    setLoading(false);
  }, [user, navigate]);

  const handleConfigureCity = (city) => {
    setSelectedCity(city);

    // Load city configuration
    const savedConfig = localStorage.getItem(`cityshot_config_${city.id}`);
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setCityConfig(convertToRelativeUnits(parsedConfig));
      } catch (error) {
        console.error('Error parsing saved config:', error);
        // Set default config if parsing fails
        setDefaultConfig(city);
      }
    } else {
      setDefaultConfig(city);
    }
  };

  // Helper function to convert absolute pixel positions to relative percentages
  const convertToRelativeUnits = (configObj) => {
    if (!configObj) return null;
    const converted = { ...configObj };

    // Handle logo position
    if (typeof converted.logoPosition?.x === 'number') {
      converted.logoPosition = {
        ...converted.logoPosition,
        x: `${converted.logoPosition.x / 10}%`
      };
    }
    if (typeof converted.logoPosition?.y === 'number') {
      converted.logoPosition = {
        ...converted.logoPosition,
        y: `${converted.logoPosition.y / 10}%`
      };
    }

    // Handle second image position
    if (typeof converted.secondImagePosition?.x === 'number' && !['left', 'center', 'right'].includes(converted.secondImagePosition.x)) {
      converted.secondImagePosition = {
        ...converted.secondImagePosition,
        x: `${converted.secondImagePosition.x / 10}%`
      };
    }
    if (typeof converted.secondImagePosition?.y === 'number') {
      converted.secondImagePosition = {
        ...converted.secondImagePosition,
        y: `${converted.secondImagePosition.y / 10}%`
      };
    }

    // Convert logo and second image size to percentage of container
    if (typeof converted.logoSize === 'number') {
      converted.logoSize = `${converted.logoSize / 4}%`;
    }
    if (typeof converted.secondImageSize === 'number') {
      converted.secondImageSize = `${converted.secondImageSize / 4}%`;
    }

    // Convert text sizes to relative viewport units
    if (typeof converted.textSize === 'number') {
      converted.textSize = `${converted.textSize / 10}vmin`;
    }
    if (typeof converted.locationSize === 'number') {
      converted.locationSize = `${converted.locationSize / 10}vmin`;
    }
    if (typeof converted.additionalTextSize === 'number') {
      converted.additionalTextSize = `${converted.additionalTextSize / 10}vmin`;
    }

    return converted;
  };

  const setDefaultConfig = (city) => {
    setCityConfig({
      // Basic settings
      primaryColor: '#3B82F6',
      secondaryColor: '#EF4444',
      customText: `Willkommen in ${city.cityName}!`,
      showLocation: true,
      showGreeting: true,
      showAdditionalText: false,
      greetingText: 'Viele Grüße aus',
      additionalText: '',
      // Background settings
      textBackground: true,
      locationBackground: true,
      additionalTextBackground: true,
      // Logo settings
      logo: '',
      logoPosition: { x: '2%', y: '2%' },
      logoSize: '20%',
      logoOpacity: 1,
      // Second image settings
      secondImage: '',
      secondImagePosition: { x: '2%', y: '2%' },
      secondImageSize: '20%',
      secondImageOpacity: 1,
      // Text overlay settings
      textPosition: { x: 'left', y: 'middle' },
      textSize: '1.8vmin',
      textOpacity: 0.9,
      textStyle: 'bold',
      fontFamily: 'Arial',
      textColor: '#FFFFFF',
      textBgColor: 'rgba(0,0,0,0.5)',
      // Location overlay settings
      locationPosition: { x: 'center', y: 'bottom' },
      locationSize: '2.2vmin',
      locationOpacity: 1,
      locationFontFamily: 'Arial',
      locationColor: '#FFFFFF',
      locationBgColor: 'rgba(0,0,0,0.5)',
      locationStyle: 'bold',
      // Additional text settings
      additionalTextPosition: { x: 'center', y: 'bottom' },
      additionalTextSize: '1.6vmin',
      additionalTextOpacity: 0.9,
      additionalFontFamily: 'Arial',
      additionalTextColor: '#FFFFFF',
      additionalTextBgColor: 'rgba(0,0,0,0.5)',
      additionalTextStyle: 'normal',
    });
  };

  const handleSaveConfig = () => {
    if (selectedCity && cityConfig) {
      localStorage.setItem(`cityshot_config_${selectedCity.id}`, JSON.stringify(cityConfig));
      alert(`Konfiguration für ${selectedCity.cityName} gespeichert!`);
    }
  };

  const handleAddCity = () => {
    const city = addCity(newCity);
    setNewCity({ email: '', cityName: '', subscription: 'trial' });
    setShowAddCity(false);
    handleConfigureCity(city);
  };

  const handleDeleteCity = (city, event) => {
    if (event) {
      event.stopPropagation(); // Prevent selecting the city when clicking delete button
    }
    setCityToDelete(city);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCity = () => {
    if (cityToDelete) {
      deleteCity(cityToDelete.id);
      
      // If the deleted city was selected, clear the selection
      if (selectedCity && selectedCity.id === cityToDelete.id) {
        setSelectedCity(null);
        setCityConfig(null);
      }
      
      setShowDeleteConfirm(false);
      setCityToDelete(null);
    }
  };

  const handleFileUpload = (type, file) => {
    if (file && cityConfig) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCityConfig(prev => ({ ...prev, [type]: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const updatePosition = (element, axis, value) => {
    setCityConfig(prev => ({
      ...prev,
      [`${element}Position`]: { ...prev[`${element}Position`], [axis]: value }
    }));
  };

  const filteredCities = cities.filter(city =>
    city.cityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    city.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const cityUrl = selectedCity ? `${window.location.origin}/#/city/${selectedCity.id}` : '';

  // Position options for dropdowns
  const horizontalPositions = [
    { value: 'left', label: 'Links' },
    { value: 'center', label: 'Mitte' },
    { value: 'right', label: 'Rechts' }
  ];

  const verticalPositions = [
    { value: 'top', label: 'Oben' },
    { value: 'middle', label: 'Mitte' },
    { value: 'bottom', label: 'Unten' }
  ];

  // Font family options
  const fontFamilies = [
    { value: 'Arial', label: 'Arial' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Helvetica', label: 'Helvetica' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Courier New', label: 'Courier New' },
    { value: 'Tahoma', label: 'Tahoma' },
    { value: 'Trebuchet MS', label: 'Trebuchet MS' }
  ];

  // Font style options
  const fontStyles = [
    { value: 'normal', label: 'Normal' },
    { value: 'bold', label: 'Fett' },
    { value: 'italic', label: 'Kursiv' },
    { value: 'bolditalic', label: 'Fett & Kursiv' }
  ];

  // Color options
  const colorOptions = [
    { value: '#FFFFFF', label: 'Weiß', className: 'bg-white border border-gray-300' },
    { value: '#000000', label: 'Schwarz', className: 'bg-black text-white' },
    { value: 'primary', label: 'Primärfarbe', dynamic: true },
    { value: 'secondary', label: 'Sekundärfarbe', dynamic: true }
  ];

  const getColorValue = (colorOption) => {
    if (colorOption === 'primary') return cityConfig?.primaryColor || '#3B82F6';
    if (colorOption === 'secondary') return cityConfig?.secondaryColor || '#EF4444';
    return colorOption;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Admin-Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <p className="text-xl text-gray-800 mb-4">Sie sind nicht angemeldet.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Zurück zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-indigo-700 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-white">
                CityShot Admin Dashboard
              </h1>
              <p className="text-indigo-200">Super Admin: {user.email}</p>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-900 transition-colors"
            >
              <SafeIcon icon={FiLogOut} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cities List */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiUsers} className="text-2xl text-indigo-600" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Städte verwalten
                  </h2>
                </div>
                <button
                  onClick={() => setShowAddCity(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <SafeIcon icon={FiPlus} /> Neue Stadt
                </button>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Stadt suchen..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                {filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <div
                      key={city.id}
                      className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                        selectedCity?.id === city.id
                          ? 'bg-indigo-50 border-indigo-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => handleConfigureCity(city)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-900">{city.cityName}</h3>
                          <p className="text-sm text-gray-600">{city.email}</p>
                          <div className="mt-1">
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                city.subscription === 'active' || city.subscription === 'enterprise'
                                  ? 'bg-green-100 text-green-800'
                                  : city.subscription === 'trial'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {city.subscription === 'active'
                                ? 'Aktiv'
                                : city.subscription === 'enterprise'
                                ? 'Enterprise'
                                : city.subscription === 'trial'
                                ? 'Test'
                                : 'Inaktiv'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteCity(city, e)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="Stadt löschen"
                        >
                          <SafeIcon icon={FiTrash2} className="text-lg" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Keine Städte gefunden
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* City Configuration */}
          <div className="lg:col-span-2">
            {selectedCity && cityConfig ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiSettings} className="text-2xl text-indigo-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Konfiguration: {selectedCity.cityName}
                    </h2>
                  </div>
                  <a
                    href={`/#/city/${selectedCity.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiEye} /> App anzeigen
                  </a>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b mb-6">
                  <button
                    className={`px-4 py-2 font-medium ${
                      activeTab === 'basic'
                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('basic')}
                  >
                    Basis-Einstellungen
                  </button>
                  <button
                    className={`px-4 py-2 font-medium ${
                      activeTab === 'overlays'
                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('overlays')}
                  >
                    Overlays & Positionen
                  </button>
                </div>

                {activeTab === 'basic' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* City Information */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-2">Stadt-Informationen</h3>
                      <div className="space-y-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Stadt-Name
                          </label>
                          <input
                            type="text"
                            value={selectedCity.cityName}
                            onChange={(e) =>
                              updateCity(selectedCity.id, { cityName: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            E-Mail
                          </label>
                          <input
                            type="email"
                            value={selectedCity.email}
                            onChange={(e) =>
                              updateCity(selectedCity.id, { email: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Abonnement
                          </label>
                          <select
                            value={selectedCity.subscription}
                            onChange={(e) =>
                              updateCity(selectedCity.id, { subscription: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="trial">Test</option>
                            <option value="active">Aktiv</option>
                            <option value="enterprise">Enterprise</option>
                            <option value="inactive">Inaktiv</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-1">App-URL:</h4>
                        <div className="bg-white p-2 rounded border border-gray-300">
                          <p className="text-sm text-blue-600 font-mono break-all">{cityUrl}</p>
                        </div>
                      </div>

                      {/* QR Code */}
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-1">QR-Code:</h4>
                        <div className="bg-white p-4 rounded border border-gray-300 flex justify-center">
                          <QRCode
                            value={cityUrl}
                            size={128}
                            bgColor={"#ffffff"}
                            fgColor={"#000000"}
                            level={"M"}
                            includeMargin={true}
                            renderAs={"svg"}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">
                          Scannen Sie den QR-Code, um die App zu öffnen
                        </p>
                      </div>
                    </div>

                    {/* Basic App Configuration */}
                    <div className="space-y-4">
                      {/* Colors */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Primärfarbe
                          </label>
                          <input
                            type="color"
                            value={cityConfig.primaryColor}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, primaryColor: e.target.value }))
                            }
                            className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sekundärfarbe
                          </label>
                          <input
                            type="color"
                            value={cityConfig.secondaryColor}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, secondaryColor: e.target.value }))
                            }
                            className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* Logo Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stadt-Logo
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload('logo', e.target.files[0])}
                            className="hidden"
                            id="admin-logo-upload"
                          />
                          <label
                            htmlFor="admin-logo-upload"
                            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
                          >
                            <SafeIcon icon={FiPlus} className="text-gray-500" /> Logo hochladen
                          </label>
                          {cityConfig.logo && (
                            <img
                              src={cityConfig.logo}
                              alt="Logo"
                              className="h-10 w-10 object-contain rounded"
                            />
                          )}
                        </div>
                      </div>

                      {/* Second Image */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Zusätzliches Bild
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload('secondImage', e.target.files[0])}
                            className="hidden"
                            id="admin-second-image-upload"
                          />
                          <label
                            htmlFor="admin-second-image-upload"
                            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
                          >
                            <SafeIcon icon={FiPlus} className="text-gray-500" /> Bild hochladen
                          </label>
                          {cityConfig.secondImage && (
                            <img
                              src={cityConfig.secondImage}
                              alt="Second"
                              className="h-10 w-10 object-contain rounded"
                            />
                          )}
                        </div>
                      </div>

                      {/* Text Settings */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Benutzerdefinierter Text
                        </label>
                        <input
                          type="text"
                          value={cityConfig.customText}
                          onChange={(e) =>
                            setCityConfig(prev => ({ ...prev, customText: e.target.value }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="z.B. Besuche unsere schöne Stadt!"
                        />
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="admin-show-location"
                            checked={cityConfig.showLocation}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, showLocation: e.target.checked }))
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-show-location" className="ml-2 text-sm text-gray-700">
                            GPS-Standort
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="admin-show-greeting"
                            checked={cityConfig.showGreeting}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, showGreeting: e.target.checked }))
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="admin-show-greeting" className="ml-2 text-sm text-gray-700">
                            Grußtext
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="admin-show-additional-text"
                            checked={cityConfig.showAdditionalText}
                            onChange={(e) =>
                              setCityConfig(prev => ({
                                ...prev,
                                showAdditionalText: e.target.checked
                              }))
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor="admin-show-additional-text"
                            className="ml-2 text-sm text-gray-700"
                          >
                            Zusatztext
                          </label>
                        </div>
                      </div>

                      {cityConfig.showGreeting && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Grußtext
                          </label>
                          <input
                            type="text"
                            value={cityConfig.greetingText}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, greetingText: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="Viele Grüße aus"
                          />
                        </div>
                      )}

                      {cityConfig.showAdditionalText && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Zusatztext (z.B. Sehenswürdigkeit)
                          </label>
                          <input
                            type="text"
                            value={cityConfig.additionalText}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, additionalText: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="z.B. Schloss Neuschwanstein"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="admin-text-background"
                            checked={cityConfig.textBackground}
                            onChange={(e) =>
                              setCityConfig(prev => ({ ...prev, textBackground: e.target.checked }))
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor="admin-text-background"
                            className="ml-2 text-sm text-gray-700"
                          >
                            Text-Hintergrund
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="admin-location-background"
                            checked={cityConfig.locationBackground}
                            onChange={(e) =>
                              setCityConfig(prev => ({
                                ...prev,
                                locationBackground: e.target.checked
                              }))
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor="admin-location-background"
                            className="ml-2 text-sm text-gray-700"
                          >
                            Standort-Hintergrund
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="admin-additional-text-background"
                            checked={cityConfig.additionalTextBackground}
                            onChange={(e) =>
                              setCityConfig(prev => ({
                                ...prev,
                                additionalTextBackground: e.target.checked
                              }))
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor="admin-additional-text-background"
                            className="ml-2 text-sm text-gray-700"
                          >
                            Zusatztext-Hintergrund
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'overlays' && (
                  <div>
                    <div className="bg-indigo-50 p-3 rounded-lg mb-6">
                      <div className="flex items-center gap-2 text-indigo-700">
                        <SafeIcon icon={FiLayers} className="text-xl" />
                        <p className="text-sm">
                          Hier können Sie die Position, Größe und Transparenz aller Overlays anpassen.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      {/* Logo Position & Size */}
                      {cityConfig.logo && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <SafeIcon icon={FiMove} className="text-indigo-600" />
                            <h3 className="font-medium">Logo-Einstellungen</h3>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Position X (%)
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={parseInt(cityConfig.logoPosition.x) || 2}
                                onChange={(e) =>
                                  updatePosition('logo', 'x', `${e.target.value}%`)
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">{cityConfig.logoPosition.x}</div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Position Y (%)
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={parseInt(cityConfig.logoPosition.y) || 2}
                                onChange={(e) =>
                                  updatePosition('logo', 'y', `${e.target.value}%`)
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">{cityConfig.logoPosition.y}</div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Größe (%)
                              </label>
                              <input
                                type="range"
                                min="5"
                                max="50"
                                value={parseInt(cityConfig.logoSize) || 20}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    logoSize: `${e.target.value}%`
                                  }))
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">{cityConfig.logoSize}</div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transparenz: {Math.round(cityConfig.logoOpacity * 100)}%
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={cityConfig.logoOpacity}
                              onChange={(e) =>
                                setCityConfig(prev => ({
                                  ...prev,
                                  logoOpacity: parseFloat(e.target.value)
                                }))
                              }
                              className="w-full"
                            />
                          </div>
                        </div>
                      )}

                      {/* Second Image Position & Size */}
                      {cityConfig.secondImage && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <SafeIcon icon={FiMove} className="text-indigo-600" />
                            <h3 className="font-medium">Zusätzliches Bild</h3>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Horizontale Position
                              </label>
                              <select
                                value={
                                  typeof cityConfig.secondImagePosition.x === 'string' &&
                                  ['left', 'center', 'right'].includes(cityConfig.secondImagePosition.x)
                                    ? cityConfig.secondImagePosition.x
                                    : 'right'
                                }
                                onChange={(e) => updatePosition('secondImage', 'x', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {horizontalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Position Y (%)
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={parseInt(cityConfig.secondImagePosition.y) || 2}
                                onChange={(e) =>
                                  updatePosition('secondImage', 'y', `${e.target.value}%`)
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">
                                {cityConfig.secondImagePosition.y}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Größe (%)
                              </label>
                              <input
                                type="range"
                                min="5"
                                max="50"
                                value={parseInt(cityConfig.secondImageSize) || 20}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    secondImageSize: `${e.target.value}%`
                                  }))
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">{cityConfig.secondImageSize}</div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transparenz: {Math.round(cityConfig.secondImageOpacity * 100)}%
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={cityConfig.secondImageOpacity}
                              onChange={(e) =>
                                setCityConfig(prev => ({
                                  ...prev,
                                  secondImageOpacity: parseFloat(e.target.value)
                                }))
                              }
                              className="w-full"
                            />
                          </div>
                        </div>
                      )}

                      {/* Custom Text Position & Style */}
                      {cityConfig.customText && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <SafeIcon icon={FiType} className="text-indigo-600" />
                            <h3 className="font-medium">Benutzerdefinierter Text</h3>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Horizontale Position
                              </label>
                              <select
                                value={cityConfig.textPosition.x}
                                onChange={(e) => updatePosition('text', 'x', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {horizontalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Vertikale Position
                              </label>
                              <select
                                value={cityConfig.textPosition.y}
                                onChange={(e) => updatePosition('text', 'y', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {verticalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schriftgröße (vmin)
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="5"
                                step="0.1"
                                value={parseFloat(cityConfig.textSize) || 1.8}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    textSize: `${e.target.value}vmin`
                                  }))
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">{cityConfig.textSize}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schriftart
                              </label>
                              <select
                                value={cityConfig.fontFamily || 'Arial'}
                                onChange={(e) =>
                                  setCityConfig(prev => ({ ...prev, fontFamily: e.target.value }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {fontFamilies.map(font => (
                                  <option key={font.value} value={font.value}>
                                    {font.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Textstil
                              </label>
                              <select
                                value={cityConfig.textStyle}
                                onChange={(e) =>
                                  setCityConfig(prev => ({ ...prev, textStyle: e.target.value }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {fontStyles.map(style => (
                                  <option key={style.value} value={style.value}>
                                    {style.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Textfarbe
                              </label>
                              <div className="flex gap-2 flex-wrap">
                                {colorOptions.map(color => (
                                  <div
                                    key={color.value}
                                    onClick={() =>
                                      setCityConfig(prev => ({ ...prev, textColor: color.value }))
                                    }
                                    className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center ${
                                      color.className
                                    } ${
                                      cityConfig.textColor === color.value
                                        ? 'ring-2 ring-offset-2 ring-indigo-500'
                                        : ''
                                    }`}
                                    style={
                                      color.dynamic
                                        ? { backgroundColor: getColorValue(color.value) }
                                        : {}
                                    }
                                  >
                                    {cityConfig.textColor === color.value && (
                                      <SafeIcon icon={FiCheck} className="text-xs" />
                                    )}
                                  </div>
                                ))}
                                <input
                                  type="color"
                                  value={
                                    cityConfig.textColor &&
                                    !colorOptions.some(c => c.value === cityConfig.textColor)
                                      ? cityConfig.textColor
                                      : '#FFFFFF'
                                  }
                                  onChange={(e) =>
                                    setCityConfig(prev => ({ ...prev, textColor: e.target.value }))
                                  }
                                  className="w-8 h-8 p-1 border-0 rounded-full cursor-pointer"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Transparenz: {Math.round(cityConfig.textOpacity * 100)}%
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={cityConfig.textOpacity}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    textOpacity: parseFloat(e.target.value)
                                  }))
                                }
                                className="w-full"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Hintergrund anzeigen
                            </label>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="text-background-toggle"
                                checked={cityConfig.textBackground}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    textBackground: e.target.checked
                                  }))
                                }
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="text-background-toggle"
                                className="ml-2 text-sm text-gray-700"
                              >
                                Text-Hintergrund aktivieren
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Location Text Settings */}
                      {cityConfig.showLocation && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <SafeIcon icon={FiMapPin} className="text-indigo-600" />
                            <h3 className="font-medium">Standort-Anzeige</h3>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Horizontale Position
                              </label>
                              <select
                                value={cityConfig.locationPosition.x}
                                onChange={(e) => updatePosition('location', 'x', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {horizontalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Vertikale Position
                              </label>
                              <select
                                value={cityConfig.locationPosition.y}
                                onChange={(e) => updatePosition('location', 'y', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {verticalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schriftgröße (vmin)
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="5"
                                step="0.1"
                                value={parseFloat(cityConfig.locationSize) || 2.2}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    locationSize: `${e.target.value}vmin`
                                  }))
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">{cityConfig.locationSize}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schriftart
                              </label>
                              <select
                                value={cityConfig.locationFontFamily || 'Arial'}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    locationFontFamily: e.target.value
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {fontFamilies.map(font => (
                                  <option key={font.value} value={font.value}>
                                    {font.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Textstil
                              </label>
                              <select
                                value={cityConfig.locationStyle || 'bold'}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    locationStyle: e.target.value
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {fontStyles.map(style => (
                                  <option key={style.value} value={style.value}>
                                    {style.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Textfarbe
                              </label>
                              <div className="flex gap-2 flex-wrap">
                                {colorOptions.map(color => (
                                  <div
                                    key={color.value}
                                    onClick={() =>
                                      setCityConfig(prev => ({ ...prev, locationColor: color.value }))
                                    }
                                    className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center ${
                                      color.className
                                    } ${
                                      cityConfig.locationColor === color.value
                                        ? 'ring-2 ring-offset-2 ring-indigo-500'
                                        : ''
                                    }`}
                                    style={
                                      color.dynamic
                                        ? { backgroundColor: getColorValue(color.value) }
                                        : {}
                                    }
                                  >
                                    {cityConfig.locationColor === color.value && (
                                      <SafeIcon icon={FiCheck} className="text-xs" />
                                    )}
                                  </div>
                                ))}
                                <input
                                  type="color"
                                  value={
                                    cityConfig.locationColor &&
                                    !colorOptions.some(c => c.value === cityConfig.locationColor)
                                      ? cityConfig.locationColor
                                      : '#FFFFFF'
                                  }
                                  onChange={(e) =>
                                    setCityConfig(prev => ({
                                      ...prev,
                                      locationColor: e.target.value
                                    }))
                                  }
                                  className="w-8 h-8 p-1 border-0 rounded-full cursor-pointer"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Transparenz: {Math.round(cityConfig.locationOpacity * 100)}%
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={cityConfig.locationOpacity}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    locationOpacity: parseFloat(e.target.value)
                                  }))
                                }
                                className="w-full"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Hintergrund anzeigen
                            </label>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="location-background-toggle"
                                checked={cityConfig.locationBackground}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    locationBackground: e.target.checked
                                  }))
                                }
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="location-background-toggle"
                                className="ml-2 text-sm text-gray-700"
                              >
                                Standort-Hintergrund aktivieren
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Additional Text Settings */}
                      {cityConfig.showAdditionalText && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <SafeIcon icon={FiType} className="text-indigo-600" />
                            <h3 className="font-medium">Zusatztext-Einstellungen</h3>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Horizontale Position
                              </label>
                              <select
                                value={cityConfig.additionalTextPosition.x}
                                onChange={(e) =>
                                  updatePosition('additionalText', 'x', e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {horizontalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Vertikale Position
                              </label>
                              <select
                                value={cityConfig.additionalTextPosition.y}
                                onChange={(e) =>
                                  updatePosition('additionalText', 'y', e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {verticalPositions.map(pos => (
                                  <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schriftgröße (vmin)
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="5"
                                step="0.1"
                                value={parseFloat(cityConfig.additionalTextSize) || 1.6}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    additionalTextSize: `${e.target.value}vmin`
                                  }))
                                }
                                className="w-full"
                              />
                              <div className="text-xs text-gray-500 mt-1">
                                {cityConfig.additionalTextSize}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schriftart
                              </label>
                              <select
                                value={cityConfig.additionalFontFamily || 'Arial'}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    additionalFontFamily: e.target.value
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {fontFamilies.map(font => (
                                  <option key={font.value} value={font.value}>
                                    {font.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Textstil
                              </label>
                              <select
                                value={cityConfig.additionalTextStyle || 'normal'}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    additionalTextStyle: e.target.value
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              >
                                {fontStyles.map(style => (
                                  <option key={style.value} value={style.value}>
                                    {style.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Textfarbe
                              </label>
                              <div className="flex gap-2 flex-wrap">
                                {colorOptions.map(color => (
                                  <div
                                    key={color.value}
                                    onClick={() =>
                                      setCityConfig(prev => ({
                                        ...prev,
                                        additionalTextColor: color.value
                                      }))
                                    }
                                    className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center ${
                                      color.className
                                    } ${
                                      cityConfig.additionalTextColor === color.value
                                        ? 'ring-2 ring-offset-2 ring-indigo-500'
                                        : ''
                                    }`}
                                    style={
                                      color.dynamic
                                        ? { backgroundColor: getColorValue(color.value) }
                                        : {}
                                    }
                                  >
                                    {cityConfig.additionalTextColor === color.value && (
                                      <SafeIcon icon={FiCheck} className="text-xs" />
                                    )}
                                  </div>
                                ))}
                                <input
                                  type="color"
                                  value={
                                    cityConfig.additionalTextColor &&
                                    !colorOptions.some(c => c.value === cityConfig.additionalTextColor)
                                      ? cityConfig.additionalTextColor
                                      : '#FFFFFF'
                                  }
                                  onChange={(e) =>
                                    setCityConfig(prev => ({
                                      ...prev,
                                      additionalTextColor: e.target.value
                                    }))
                                  }
                                  className="w-8 h-8 p-1 border-0 rounded-full cursor-pointer"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Transparenz: {Math.round(cityConfig.additionalTextOpacity * 100)}%
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={cityConfig.additionalTextOpacity}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    additionalTextOpacity: parseFloat(e.target.value)
                                  }))
                                }
                                className="w-full"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Hintergrund anzeigen
                            </label>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="additional-text-background-toggle"
                                checked={cityConfig.additionalTextBackground}
                                onChange={(e) =>
                                  setCityConfig(prev => ({
                                    ...prev,
                                    additionalTextBackground: e.target.checked
                                  }))
                                }
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="additional-text-background-toggle"
                                className="ml-2 text-sm text-gray-700"
                              >
                                Zusatztext-Hintergrund aktivieren
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <button
                    onClick={handleSaveConfig}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                  >
                    <SafeIcon icon={FiSave} /> Konfiguration speichern
                  </button>
                </div>

                {/* Preview */}
                <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-4">Live-Vorschau</h3>
                  <div className="bg-gray-800 rounded-lg p-4 aspect-[4/3] max-w-md mx-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                      <span className="text-gray-400">Kamera-Vorschau</span>
                    </div>

                    {/* Overlay Preview */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Logo */}
                      {cityConfig.logo && (
                        <div
                          style={{
                            position: 'absolute',
                            left:
                              typeof cityConfig.logoPosition.x === 'string'
                                ? cityConfig.logoPosition.x
                                : `${cityConfig.logoPosition.x}px`,
                            top:
                              typeof cityConfig.logoPosition.y === 'string'
                                ? cityConfig.logoPosition.y
                                : `${cityConfig.logoPosition.y}px`,
                            opacity: cityConfig.logoOpacity,
                            height:
                              typeof cityConfig.logoSize === 'string'
                                ? cityConfig.logoSize
                                : `${cityConfig.logoSize}px`,
                            width: 'auto'
                          }}
                        >
                          <img
                            src={cityConfig.logo}
                            alt="Logo"
                            className="h-full w-auto object-contain"
                          />
                        </div>
                      )}

                      {/* Second Image */}
                      {cityConfig.secondImage && (
                        <div
                          style={{
                            position: 'absolute',
                            ...(cityConfig.secondImagePosition.x === 'left'
                              ? { left: '2%' }
                              : cityConfig.secondImagePosition.x === 'center'
                              ? { left: '50%', transform: 'translateX(-50%)' }
                              : cityConfig.secondImagePosition.x === 'right'
                              ? { right: '2%' }
                              : {
                                  left:
                                    typeof cityConfig.secondImagePosition.x === 'string'
                                      ? cityConfig.secondImagePosition.x
                                      : `${cityConfig.secondImagePosition.x}px`
                                }),
                            top:
                              typeof cityConfig.secondImagePosition.y === 'string'
                                ? cityConfig.secondImagePosition.y
                                : `${cityConfig.secondImagePosition.y}px`,
                            opacity: cityConfig.secondImageOpacity,
                            height:
                              typeof cityConfig.secondImageSize === 'string'
                                ? cityConfig.secondImageSize
                                : `${cityConfig.secondImageSize}px`,
                            width: 'auto'
                          }}
                        >
                          <img
                            src={cityConfig.secondImage}
                            alt="Second"
                            className="h-full w-auto object-contain"
                          />
                        </div>
                      )}

                      {/* Custom Text */}
                      {cityConfig.customText && (
                        <div
                          style={{
                            position: 'absolute',
                            ...(cityConfig.textPosition.x === 'left'
                              ? { left: '2%', textAlign: 'left' }
                              : cityConfig.textPosition.x === 'center'
                              ? { left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }
                              : cityConfig.textPosition.x === 'right'
                              ? { right: '2%', textAlign: 'right' }
                              : {
                                  left:
                                    typeof cityConfig.textPosition.x === 'string'
                                      ? cityConfig.textPosition.x
                                      : `${cityConfig.textPosition.x}px`
                                }),
                            ...(cityConfig.textPosition.y === 'top'
                              ? { top: '2%' }
                              : cityConfig.textPosition.y === 'middle'
                              ? {
                                  top: '50%',
                                  transform: `${
                                    cityConfig.textPosition.x === 'center'
                                      ? 'translate(-50%,-50%)'
                                      : 'translateY(-50%)'
                                  }`
                                }
                              : cityConfig.textPosition.y === 'bottom'
                              ? { bottom: '2%' }
                              : {
                                  top:
                                    typeof cityConfig.textPosition.y === 'string'
                                      ? cityConfig.textPosition.y
                                      : `${cityConfig.textPosition.y}px`
                                }),
                            opacity: cityConfig.textOpacity,
                            fontSize: cityConfig.textSize,
                            fontWeight: cityConfig.textStyle.includes('bold') ? 'bold' : 'normal',
                            fontStyle: cityConfig.textStyle.includes('italic') ? 'italic' : 'normal',
                            fontFamily: cityConfig.fontFamily || 'Arial',
                            padding: '10px',
                            borderRadius: '8px',
                            backgroundColor: cityConfig.textBackground
                              ? 'rgba(0,0,0,0.5)'
                              : 'transparent',
                            color: getColorValue(cityConfig.textColor) || 'white',
                            maxWidth: '80%'
                          }}
                        >
                          {cityConfig.customText}
                        </div>
                      )}

                      {/* Location with Greeting */}
                      {cityConfig.showLocation && (
                        <div
                          style={{
                            position: 'absolute',
                            ...(cityConfig.locationPosition.x === 'left'
                              ? { left: '2%', textAlign: 'left' }
                              : cityConfig.locationPosition.x === 'center'
                              ? { left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }
                              : cityConfig.locationPosition.x === 'right'
                              ? { right: '2%', textAlign: 'right' }
                              : {
                                  left:
                                    typeof cityConfig.locationPosition.x === 'string'
                                      ? cityConfig.locationPosition.x
                                      : `${cityConfig.locationPosition.x}px`
                                }),
                            ...(cityConfig.locationPosition.y === 'top'
                              ? { top: '2%' }
                              : cityConfig.locationPosition.y === 'middle'
                              ? {
                                  top: '50%',
                                  transform: `${
                                    cityConfig.locationPosition.x === 'center'
                                      ? 'translate(-50%,-50%)'
                                      : 'translateY(-50%)'
                                  }`
                                }
                              : cityConfig.locationPosition.y === 'bottom'
                              ? { bottom: '2%' }
                              : {
                                  top:
                                    typeof cityConfig.locationPosition.y === 'string'
                                      ? cityConfig.locationPosition.y
                                      : `${cityConfig.locationPosition.y}px`
                                }),
                            opacity: cityConfig.locationOpacity,
                            padding: '10px',
                            borderRadius: '8px',
                            backgroundColor: cityConfig.locationBackground
                              ? 'rgba(0,0,0,0.5)'
                              : 'transparent',
                            color: getColorValue(cityConfig.locationColor) || 'white',
                            fontFamily: cityConfig.locationFontFamily || 'Arial'
                          }}
                        >
                          {cityConfig.showGreeting && (
                            <div
                              style={{
                                fontSize:
                                  typeof cityConfig.locationSize === 'string'
                                    ? `calc(${cityConfig.locationSize} * 0.8)`
                                    : Math.max((cityConfig.locationSize || 22) * 0.8, 12) + 'px',
                                fontWeight: cityConfig.locationStyle?.includes('bold')
                                  ? 'bold'
                                  : 'normal',
                                fontStyle: cityConfig.locationStyle?.includes('italic')
                                  ? 'italic'
                                  : 'normal'
                              }}
                            >
                              {cityConfig.greetingText}
                            </div>
                          )}

                          <div
                            className="flex items-center justify-center gap-2"
                            style={{
                              fontSize: cityConfig.locationSize,
                              fontWeight: cityConfig.locationStyle?.includes('bold')
                                ? 'bold'
                                : 'normal',
                              fontStyle: cityConfig.locationStyle?.includes('italic')
                                ? 'italic'
                                : 'normal'
                            }}
                          >
                            <SafeIcon icon={FiMapPin} />
                            {selectedCity.cityName}
                          </div>

                          {/* Additional Text */}
                          {cityConfig.showAdditionalText && cityConfig.additionalText && (
                            <div
                              style={{
                                fontSize: cityConfig.additionalTextSize,
                                marginTop: '4px',
                                opacity: cityConfig.additionalTextOpacity,
                                fontFamily: cityConfig.additionalFontFamily || 'Arial',
                                color: getColorValue(cityConfig.additionalTextColor) || 'white',
                                fontWeight: cityConfig.additionalTextStyle?.includes('bold')
                                  ? 'bold'
                                  : 'normal',
                                fontStyle: cityConfig.additionalTextStyle?.includes('italic')
                                  ? 'italic'
                                  : 'normal',
                                backgroundColor: cityConfig.additionalTextBackground
                                  ? 'rgba(0,0,0,0.3)'
                                  : 'transparent',
                                padding: cityConfig.additionalTextBackground ? '4px 8px' : '0',
                                borderRadius: cityConfig.additionalTextBackground ? '4px' : '0'
                              }}
                            >
                              {cityConfig.additionalText}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center justify-center h-full">
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiSettings} className="text-2xl text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Stadt ausgewählt</h3>
                  <p className="text-gray-600 mb-4">
                    Wählen Sie eine Stadt aus der Liste aus, um deren Konfiguration zu bearbeiten.
                  </p>
                  <button
                    onClick={() => setShowAddCity(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors mx-auto"
                  >
                    <SafeIcon icon={FiPlus} /> Neue Stadt anlegen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add City Modal */}
      {showAddCity && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Neue Stadt hinzufügen
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stadt-Name
                </label>
                <input
                  type="text"
                  value={newCity.cityName}
                  onChange={(e) => setNewCity(prev => ({ ...prev, cityName: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="z.B. Berlin"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail
                </label>
                <input
                  type="email"
                  value={newCity.email}
                  onChange={(e) => setNewCity(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="berlin@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Abonnement
                </label>
                <select
                  value={newCity.subscription}
                  onChange={(e) => setNewCity(prev => ({ ...prev, subscription: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="trial">Test</option>
                  <option value="active">Aktiv</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleAddCity}
                  disabled={!newCity.cityName || !newCity.email}
                  className={`flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors ${
                    !newCity.cityName || !newCity.email ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Stadt hinzufügen
                </button>
                <button
                  onClick={() => setShowAddCity(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full"
          >
            <div className="flex items-center gap-3 mb-4 text-red-600">
              <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
              <h2 className="text-xl font-bold">Stadt löschen</h2>
            </div>
            <p className="text-gray-700 mb-6">
              Möchten Sie die Stadt <span className="font-bold">{cityToDelete?.cityName}</span> wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDeleteCity}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiTrash2} /> Löschen bestätigen
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCityToDelete(null);
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminDashboard;